import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { Utils } from 'formiojs';
import _cloneDeep from 'lodash/cloneDeep';
import { ToastrService } from 'ngx-toastr';
import {
	BehaviorSubject,
	Observable,
	Subscription,
	catchError,
	filter,
	finalize,
	forkJoin,
	map,
	of,
	take,
	tap,
} from 'rxjs';
import * as DeleteConfirmationDialog from 'dp-formio';
import {
	ConfirmationDialogComponent,
	FormioCustomComponent,
	FormioLoader,
	ResourceNameService,
	SuccessToast,
	TRANSLATIONS,
} from 'dp-formio';
import { AddEditResourceDialog } from './add-edit-dialog/add-edit-dialog.component';

@Component({
	selector: 'app-master-details-resource-wrapper',
	templateUrl: './master-details-resource-wrapper.component.html',
	encapsulation: ViewEncapsulation.None,
	providers: [FormioLoader],
})
export class MasterDetailsResourceWrapperComponent
	implements FormioCustomComponent<any>, OnDestroy
{
	dir: 'rtl' | 'ltr';
	@Input() value: any = {};
	@Output() valueChange = new EventEmitter<any>();
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;
	@Input() data: any;
	@Input() customOptions: any;
	@Input() formioComponent: any;
	private sub: Subscription;
	name: string;
	readonly dataSource: BehaviorSubject<any[]>;
	readonly treeSource: MatTreeNestedDataSource<any>;
	/** tree control */
	readonly treeControl = new NestedTreeControl<any>((node) => node.childrens);
	readonly hasChild = (_: number, node: any) =>
		!!node.childrens && node.childrens.length > 0;
	readonly trackBy = (_: number, node: any) => node.id;

	deletedNodesObs$: Array<Observable<any>> = [];

	constructor(
		private toastrService: ToastrService,
		private formloader: FormioLoader,
		private resourceNameService: ResourceNameService,
		private translateService: TranslateService,
		private dialog: MatDialog
	) {
		this.formloader.loading = false;
		this.treeSource = new MatTreeNestedDataSource<any>();
		this.dataSource = new BehaviorSubject<any[]>([]);
		this.dataSource.subscribe((items) => {
			this.treeSource.data = null;

			const sortedParentNodes = items.sort(function (a, b) {
				if (a.order < b.order) {
					return -1;
				} else if (a.order > b.order) {
					return 1;
				} else {
					return 0;
				}
			});

			sortedParentNodes.forEach((resource: any) => {
				if (resource.childrens) {
					resource.childrens.sort(function (a, b) {
						if (a.order < b.order) {
							return -1;
						} else if (a.order > b.order) {
							return 1;
						} else {
							return 0;
						}
					});
				}
			});

			this.treeSource.data = sortedParentNodes;
		});
		this.dataSource.next([]);
		this.notifyValue();
		this.changeDirection(this.translateService.currentLang);
		this.sub = this.translateService.onLangChange.subscribe((data) => {
			this.changeDirection(data.lang);
		});
		this.sub = this.checklistSelection.changed.subscribe((data) => {
			console.log(data.source.selected);
		});

		setTimeout(() => {
			if (this.customOptions) {
				this.name = Object.keys(this.customOptions)[0];
				if (this.value) {
					this.fillData();
				}
			}
		});
	}

	private changeDirection(lang) {
		this.dir = null;
		setTimeout(() => {
			this.dir = lang == 'ar' ? 'rtl' : 'ltr';
		});
	}

	dialogConfig = (prop, node?) => {
		return {
			dir: this.dir,
			isAdd: prop.isAdd,
			name: prop.name ?? node.translatedName,
			fields: prop.fields ?? _cloneDeep(node.fields),
			icon: prop.icon ?? node.icon,
			color: prop.color ?? node.color,
			isView: prop.viewMode ?? false,
			data: this.data,
			parentId: prop.parentId ?? node.parentId,
			isParentNode: prop.isParentNode ?? node.isParentNode,
			resourceName: prop.resourceName ?? node.resourceName,
			order: prop.order ?? node.order,
		};
	};
	itemSetup = (prop, node?) => {
		return {
			id: prop.id ?? node.id,
			name: prop.name ?? node.prop,
			translatedName: prop.translatedName ?? node.translatedName,
			fields: prop.fields ?? node.fields,
			icon: prop.icon ?? node.icon,
			color: prop.color ?? node.color,
			actions: prop.actions ?? node.actions,
			childrens: prop.children ?? node.childrens,
			_id: prop._id ?? node._id,
			isParentNode: prop.isParentNode ?? node.isParentNode,
			parentId: prop.parentId ?? node.parentId,
			resourceName: prop.resourceName ?? node.resourceName,
			order: prop.order ?? node.order,
		};
	};

	createDialog(isAdd: boolean, level: any, parentNode = null) {
		const dialogRef = this.dialog.open(AddEditResourceDialog, {
			width: 'min(960px,80%)',
			panelClass: 'custom-dialog-container',
			data: {
				...this.dialogConfig({
					isAdd: isAdd,
					name: Object.keys(level)[0],
					fields: _cloneDeep(level[Object.keys(level)[0]].fields),
					icon: level[Object.keys(level)[0]].icon,
					color: level[Object.keys(level)[0]].color,
					data: this.data,
					parentId: level[Object.keys(level)[0]].parentId,
					isParentNode: parentNode ? parentNode._id : true,
					resourceName: level[Object.keys(level)[0]].resourceName,
					order: level[Object.keys(level)[0]].order,
				}),
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.fillSource(result, level, parentNode);
			}
		});
	}

	editDialog(node: any) {
		const dialogRef = this.dialog.open(AddEditResourceDialog, {
			width: '80%',
			data: {
				...this.dialogConfig({ isAdd: false }, node),
				_id: node._id,
				resourceName: node.translatedName,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				const colorStyle = result.color
					? `color: ${result?.color} !important;`
					: '';
				const icon = result.icon
					? `<i class="${result.icon}" style="${colorStyle}"></i>`
					: '';
				const item = this.itemSetup(
					{
						name:
							`${
								icon || ''
							} <strong style="${colorStyle}">${this.translateService.instant(
								`forms.common.${node.translatedName}`
							)} :</strong>` +
							result.fields
								.filter((q) => q.viewInTree && q.value)
								.map((q) => q.value)
								.join(' • '),
						fields: result.fields,
					},
					node
				);
				this.findInDataSourceAndUpdate(this.dataSource.value, item);
				this.dataSource.next([...this.dataSource.value]);
				this.notifyValue();
			}
		});
	}

	viewDialog(node: any) {
		this.dialog.open(AddEditResourceDialog, {
			width: '80%',
			data: this.dialogConfig({ viewMode: true }, node),
		});
	}

	fillSource(result: any, level: any, parentNode = null) {
		const colorStyle = result.color
			? `color: ${result?.color} !important;`
			: '';
		const icon = result?.icon
			? `<i class="${result?.icon}" style="${colorStyle}"></i>`
			: '';
		const newItem = this.itemSetup(
			{
				id: new Date().getTime(),
				name:
					`${
						icon || ''
					} <strong style="${colorStyle}">${this.translateService.instant(
						`forms.common.${Object.keys(level)[0]}`
					)} : </strong>` +
					result.fields
						.filter((q) => q.viewInTree && q.value)
						.map((q) => q.value)
						.join(' • '),
				translatedName: Object.keys(level)[0],
				actions: level[Object.keys(level)[0]].actions,
				parentId: level[Object.keys(level)[0]].parentId,
				resourceName: level[Object.keys(level)[0]].resourceName,
				children: [],
			},
			result
		);
		if (parentNode) {
			this.findInDataSourceAndFill(this.dataSource.value, parentNode, newItem);
			this.dataSource.next([...this.dataSource.value]);
			this.notifyValue();
			return newItem;
		} else {
			this.dataSource.next([
				...this.dataSource.value,
				{
					...newItem,
				},
			]);
			this.notifyValue();
			return newItem;
		}
	}

	createNode(parentNode, bindWith) {
		const node = this.findNode(this.customOptions, bindWith);
		this.createDialog(true, node, parentNode);
	}

	findNode(startedPoint, bindWith) {
		for (let item in startedPoint) {
			if (item == bindWith) return startedPoint;
			else {
				const branchOfNods = startedPoint[item].childrens.find((q) =>
					this.findNode(q, bindWith)
				);
				if (branchOfNods) return this.findNode(branchOfNods, bindWith);
			}
		}
	}

	findInDataSourceAndFill(startedPoint, parentNode, newItem) {
		startedPoint.forEach((q) => {
			if (q.id == parentNode.id) q.childrens.push(newItem);
			else this.findInDataSourceAndFill(q.childrens, parentNode, newItem);
		});
	}

	findInDataSourceAndUpdate(startedPoint, item) {
		for (let index = 0; index < startedPoint.length; index++) {
			if (startedPoint[index].id == item.id) {
				startedPoint[index] = { ...item };
				if (startedPoint[index].order) {
					const field = item.fields.filter((q) => q.name === 'order');
					startedPoint[index].order = +field[0].value;
				}
			} else
				this.findInDataSourceAndUpdate(startedPoint[index].childrens, item);
		}
	}

	private _openConfirmDialog(
		message = '',
		ok: string = null,
		cancel: string = null
	) {
		return this.dialog
			.open(ConfirmationDialogComponent, {
				width: 'min(80% , 350px)',
				direction: this.dir,
				restoreFocus: false,
				data: {
					message: message,
					buttonText: {
						cancel:
							cancel ?? this.translateService.instant('forms.common.cancel'),
						ok: ok ?? this.translateService.instant('forms.common.yes'),
					},
				},
			})
			.afterClosed()
			.pipe(
				take(1),
				filter((rs) => !!rs)
			);
	}

	removeNode(node) {
		this._openConfirmDialog(
			this.dir == 'rtl'
				? 'هل انت متأكد من حذف هذا العنصر؟'
				: 'Are you sure you want to delete?'
		).subscribe((res) => {
			if (node.childrens && node.childrens.length) {
				node.childrens.forEach((childNode) => {
					this.addDeletedNodesObs(childNode);
				});
				this.deleteBulKNodes(node);
			} else {
				this.deleteResourceData(node);
			}
		});
	}

	addDeletedNodesObs(node: any) {
		this.deletedNodesObs$.push(
			this.resourceNameService.deleteResourceData(node.resourceName, node._id)
		);
	}

	deleteBulKNodes(node: any) {
		this.formloader.loading = true;
		forkJoin([...this.deletedNodesObs$])
			.pipe(
				tap((res) => {
					if (res) {
						this.deleteResourceData(node, true);
					}
				}),
				finalize(() => {
					this.formloader.loading = false;
				}),
				catchError((error) => {
					this.deletedNodesObs$ = [];
					return of(0);
				})
			)
			.subscribe();
	}

	deleteResourceData(node: any, isBulk = false) {
		this.formloader.loading = true;
		this.resourceNameService
			.deleteResourceData(node.resourceName, node._id)
			.subscribe(
				(res: any) => {
					if (res) {
						this.deletNodesFromUI(node);
						this.toastrService.show(
							this.translateService.instant('Deletion Message'),
							'',
							{
								toastClass: 'notification-toast',
								closeButton: true,
								enableHtml: true,
								toastComponent: SuccessToast,
							}
						);
					}
				},
				() => (this.formloader.loading = false),
				() => {
					if (isBulk) {
						this.deletedNodesObs$ = [];
					}
				}
			);
	}

	deletNodesFromUI(node: any) {
		const data = this.dataSource.value;
		this.dataSource.next(this.deepRemoveNode(_cloneDeep(data), node));
		this.checkRelatedEntityWhenDelete(
			this.getRemovedNode(_cloneDeep(data), node)
		);
		this.checklistSelection.clear();
		this.notifyValue();
		this.formloader.loading = false;
	}

	deepRemoveNode(data: any[], node: any | any[]) {
		const result = [];
		data.forEach((item) => {
			if (item.childrens && item.childrens.length)
				item.childrens = this.deepRemoveNode(item.childrens, node);
			if (node instanceof Array) {
				if (!node.find((q) => q.id == item.id)) result.push(item);
			} else if (item.id != node.id) result.push(item);
		});
		return result;
	}

	getRemovedNode(data: any[], node: any | any[], removedNodes: any[] = []) {
		data.forEach((item) => {
			if (item.childrens && item.childrens.length)
				this.getRemovedNode(item.childrens, node, removedNodes);

			if (item.id && item.id == node.id && item.fields && item.fields.length)
				removedNodes.push(item.fields[0].value);
		});

		return removedNodes;
	}

	removeBulk() {
		DeleteConfirmationDialog.build(
			this.formioComponent,
			() => {
				const data = this.dataSource.value;
				this.dataSource.next(
					this.deepRemoveNode(
						_cloneDeep(data),
						this.checklistSelection.selected
					)
				);
				this.checkRelatedEntityWhenDelete(
					this.getRemovedNode(
						_cloneDeep(data),
						this.checklistSelection.selected
					)
				);
				this.checklistSelection.clear();
				this.notifyValue();
			},
			null,
			'Are you sure to delete this items',
			'All children under this items will be deleted'
		);
	}

	hasAction(node, action) {
		return !!node.actions.find((q) => q.name == action);
	}

	private checkRelatedEntityWhenDelete(removedNodes: any[]) {
		if (this.customOptions.relatedEntityWhenDelete) {
			this.customOptions.relatedEntityWhenDelete.forEach((entity) => {
				const components = Utils.searchComponents(
					this.formioComponent.root.components,
					{ key: entity.key }
				);
				components.forEach((component) => {
					const data = this.formioComponent.evaluate(entity.value, {
						value: component.getValue(),
						removedNodes: removedNodes,
					});
					component.setValue(data);
					component.triggerRedraw();
				});

				const nestedForms = Utils.searchComponents(
					this.formioComponent.root.components,
					{ type: 'form' }
				);
				nestedForms.forEach((formComponent) => {
					formComponent.subFormReady.then((subForm) => {
						const formComponents =
							formComponent.component.display == 'wizard'
								? subForm.getPages({ all: true })
								: subForm.components;
						Utils.eachComponent(
							formComponents,
							(component) => {
								if (component.key == entity.key) {
									const value = this.formioComponent.evaluate(entity.value, {
										value: component.getValue(),
										removedNodes: removedNodes,
									});
									component.setValue(value);
									component.triggerRedraw();
								}
							},
							true
						);
					});
				});
			});
		}
	}

	private notifyValue() {
		const resources = [];
		for (const temp of this.dataSource.value) {
			let item = this.getFiledsValue(temp);
			this.getChildrensValue(item, temp);
			resources.push(item);
		}
		this.value = { resources };
		this.valueChange.emit(this.value);
		this.afterChangeValue();
	}

	private afterChangeValue() {
		if (this.customOptions && this.customOptions.afterChange)
			this.customOptions.afterChange.forEach((item) => {
				const components = Utils.searchComponents(
					this.formioComponent.root.components,
					{ key: item.key }
				);
				components.forEach((c) => {
					const value = this.formioComponent.interpolate(item.value, {
						data: this.data,
					});
					c.setValue(value);
				});

				const nestedForms = Utils.searchComponents(
					this.formioComponent.root.components,
					{ type: 'form' }
				);
				nestedForms.forEach((formComponent) => {
					formComponent.subFormReady.then((subForm) => {
						Utils.eachComponent(
							subForm.components,
							(component) => {
								if (component.key == item.key) {
									const value = this.formioComponent.interpolate(item.value, {
										data: this.data,
									});
									component.setValue(value);
								}
							},
							true
						);
					});
				});
			});
	}

	private getFiledsValue(node) {
		let item = {};
		const fileds = node.fields.map((q) => {
			return { [q.name]: q.value };
		});
		fileds.forEach((prop) => {
			for (const [key, value] of Object.entries(prop)) {
				item[key] = value;
			}
		});
		return item;
	}

	private getChildrensValue(item, node) {
		const subProps = node.actions
			.filter((q) => q.bindWith)
			.map((q) => q.bindWith);
		subProps.forEach((key) => {
			item[key] = [];
		});
		if (node?.childrens?.length > 0) {
			node.childrens.forEach((q) => {
				const child = this.getFiledsValue(q);
				if (q.childrens.length > 0) this.getChildrensValue(child, q);
				if (item && item[q.translatedName]) item[q.translatedName].push(child);
			});
		}
	}

	private fillData() {
		if (this.value) {
			const resourceData = [];
			for (const prop of Object.keys(this.value)) {
				resourceData.push(this.value[prop]);
			}

			resourceData.forEach((data) => {
				const level = this.customOptions;
				const item = {
					_id: data._id,
					order: data.data.order,
					fields: _cloneDeep(level[Object.keys(level)[0]].fields),
					icon: level[Object.keys(level)[0]].icon,
					color: level[Object.keys(level)[0]].color,
					childrens: [],
				};

				this.setFieldsTranslations(item.fields, data, false);

				const currentNode = this.fillSource(item, level);
				level[Object.keys(level)[0]].actions
					.filter((q) => q.bindWith)
					.forEach((element) => {
						if (data.data[element.bindWith]) {
							const node = this.findNode(this.customOptions, element.bindWith);
							this.findAndFillSubData(
								data.data[element.bindWith],
								node,
								currentNode
							);
						}
					});
			});
		}
	}

	private findAndFillSubData(array, level, parrentNode) {
		array.forEach((data) => {
			const item = {
				_id: data._id,
				order: data.order,
				isParentNode: parrentNode ? parrentNode._id : true,
				fields: _cloneDeep(level[Object.keys(level)[0]].fields),
				icon: level[Object.keys(level)[0]].icon,
				color: level[Object.keys(level)[0]].color,
				childrens: [],
			};

			this.setFieldsTranslations(item.fields, data);

			const currentNode = this.fillSource(item, level, parrentNode);
			level[Object.keys(level)[0]].actions
				.filter((q) => q.bindWith)
				.forEach((element) => {
					if (data[element.bindWith]) {
						const node = this.findNode(this.customOptions, element.bindWith);
						this.findAndFillSubData(data[element.bindWith], node, currentNode);
					}
				});
		});
	}

	setFieldsTranslations(fields, field, isChild = true) {
		this.translateService
			.get('key')
			.pipe(
				take(1),
				map((_) => this.translateService.store.translations)
			)
			.subscribe(({ ar, en }) => {
				fields.forEach((prop) => {
					if (!isChild) {
						if (field.data[prop.name]) prop.value = field.data[prop.name];
					} else {
						if (field[prop.name]) prop.value = field[prop.name];
					}

					//Set Translations
					const key = !isChild ? field.data.key : field.key;
					if (prop.name.includes(TRANSLATIONS.NAMEAR)) {
						prop.value = ar?.forms?.common?.[key] || '';
					} else if (prop.name.includes(TRANSLATIONS.NAMEEN)) {
						prop.value = en?.forms?.common?.[key] || '';
					}
				});
			});
	}

	/* Selection Code */
	checklistSelection = new SelectionModel<any>(true);
	getLevel = (node: any) => node.level;

	todoLeafItemSelectionToggle(node: any): void {
		this.checklistSelection.toggle(node);
		this.checkAllParentsSelection(node);
	}

	checkAllParentsSelection(node: any): void {
		let parent: any | null = this.getParentNode(node);
		while (parent) {
			this.checkRootNodeSelection(parent);
			parent = this.getParentNode(parent);
		}
	}

	getParentNode(node: any): any | null {
		const currentLevel = this.getLevel(node);

		if (currentLevel < 1) {
			return null;
		}

		if (!this.treeControl.dataNodes) return;
		const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

		for (let i = startIndex; i >= 0; i--) {
			const currentNode = this.treeControl.dataNodes[i];

			if (this.getLevel(currentNode) < currentLevel) {
				return currentNode;
			}
		}
		return null;
	}

	checkRootNodeSelection(node: any): void {
		const nodeSelected = this.checklistSelection.isSelected(node);
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 &&
			descendants.every((child) => {
				return this.checklistSelection.isSelected(child);
			});
		if (nodeSelected && !descAllSelected) {
			this.checklistSelection.deselect(node);
		} else if (!nodeSelected && descAllSelected) {
			this.checklistSelection.select(node);
		}
	}

	descendantsAllSelected(node: any): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 &&
			descendants.every((child) => {
				return this.checklistSelection.isSelected(child);
			});
		// if (descAllSelected) this.checklistSelection.select(node);
		// else this.checklistSelection.deselect(node);
		return descAllSelected;
	}

	descendantsPartiallySelected(node: any): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const result = descendants.some((child) =>
			this.checklistSelection.isSelected(child)
		);
		return result && !this.descendantsAllSelected(node);
	}

	todoItemSelectionToggle(node: any): void {
		this.checklistSelection.toggle(node);
		const descendants = this.treeControl.getDescendants(node);
		this.checklistSelection.isSelected(node)
			? this.checklistSelection.select(...descendants)
			: this.checklistSelection.deselect(...descendants);

		// Force update for the parent
		descendants.forEach((child) => this.checklistSelection.isSelected(child));
		this.checkAllParentsSelection(node);
	}

	/* End Selection Code */

	ngOnDestroy() {
		if (this.sub) this.sub.unsubscribe();
		this.deletedNodesObs$ = [];
	}
}
